import type { CID } from 'multiformats/cid';

import type { CidDownloadLogger } from '@/services/abstract/poc/CidDownloadLogger';
import type { AddFileResult, HeliaNode, ProgressCallback } from '@/types/poc/helia';

export interface GetFileOptions {
  onProgress?: ProgressCallback;
  downloadLogger?: CidDownloadLogger;
  expectedSize?: number;
  signal?: AbortSignal;
}

export abstract class FileStoreService {
  abstract addFile(
    node: HeliaNode,
    file: File,
    onProgress?: ProgressCallback
  ): Promise<AddFileResult>;

  abstract getFile(
    node: HeliaNode,
    cid: string | CID,
    options?: GetFileOptions
  ): Promise<Uint8Array>;

  abstract pinFile(node: HeliaNode, cid: string | CID): Promise<void>;
  abstract unpinFile(node: HeliaNode, cid: string | CID): Promise<void>;
  abstract isPinned(node: HeliaNode, cid: string | CID): Promise<boolean>;

  /**
   * Unpins a file's DAG so its blocks become reclaimable by garbage collection.
   * Walks offline (never reaches for content routing) and is a silent no-op for a
   * CID that isn't pinned locally. Returns whether the CID was pinned beforehand.
   * Reclaim the freed space with {@link runGarbageCollection}.
   */
  abstract unpinLocalFile(node: HeliaNode, cid: string | CID): Promise<boolean>;

  /**
   * Reclaims every unpinned block from the local blockstore (`helia.gc()`). This
   * is a global sweep — it also evicts transient unpinned read-cache — so run it
   * once per batch after unpinning, not per file.
   */
  abstract runGarbageCollection(node: HeliaNode): Promise<void>;

  abstract verifyDagIntegrity(
    node: HeliaNode,
    cid: CID,
    expectedSize?: number,
    expectedHash?: string
  ): Promise<DagIntegrityResult>;
}

export type DagIntegrityResult =
  | { valid: true; byteCount: number }
  | { valid: false; error: string };
